/**
 * Renovação de token indisponível por causa TRANSIENTE — rede fora, 5xx, 408, 425 ou 429.
 *
 * Existe para separar duas coisas que o código anterior tratava como uma só, com consequências
 * opostas para o usuário: um refresh token revogado (definitivo, a sessão acabou) e um 502 de
 * dois segundos (transitório, a sessão está viva). Tratar o segundo como o primeiro apagava a
 * sessão de quem não tinha feito nada de errado, sem uma única retentativa.
 *
 * Quem recebe este erro deve deixar o 401 original chegar à tela como erro de requisição, e NÃO
 * declarar a sessão morta.
 *
 * Mora AQUI, e não no adapter, de propósito: o ApiClient precisa do tipo para decidir, e importá-lo
 * do adapter fechava o ciclo services/ApiClient → adapters/AzimAuthAdapter → data/authRepo →
 * services/ApiClient. Hoje seria benigno (o binding só é usado em tempo de chamada), mas o Vite
 * silencia o aviso de dependência circular do Rollup, então a armadilha ficaria armada e muda.
 * AuthPort não importa nada.
 */
export class RefreshTemporarilyUnavailableError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "RefreshTemporarilyUnavailableError";
  }
}

/**
 * adapters/auth/AuthPort — contrato de autenticação estável para as camadas
 * superiores (DD-003, RNF 1). Nenhum tipo do Firebase atravessa esta fronteira:
 * o portal conhece apenas `AuthPort`/`AuthSession`, nunca `firebase/auth`.
 */

/** Sessão autenticada exposta ao app (sem tipos de fornecedor). */
export interface AuthSession {
  readonly uid: string;
  readonly email: string;
  /** ID token (JWT) — mantido apenas em memória/sessionStorage, nunca em localStorage. */
  readonly token: string;
}

/**
 * Desafio de segundo fator (2FA por e-mail): a primeira etapa do login validou as
 * credenciais mas exige um código OTP enviado por e-mail. O cliente deve chamar
 * `verifyOtp(challengeId, code)` para concluir e obter a sessão.
 */
export interface OtpChallenge {
  readonly kind: "otp_challenge";
  readonly challengeId: string;
}

/**
 * Resultado da primeira etapa do login:
 * - uma `AuthSession` quando o 2FA está desabilitado (login direto), ou
 * - um `OtpChallenge` quando o 2FA está habilitado (exige verificação).
 */
export type SignInResult = AuthSession | OtpChallenge;

/** Type guard: o resultado do login é um desafio OTP (segundo fator). */
export function isOtpChallenge(result: SignInResult): result is OtpChallenge {
  return (result as OtpChallenge).kind === "otp_challenge";
}

/**
 * Resultado do aceite de convite.
 * Deliberadamente NÃO é uma `AuthSession`: o aceite grava a credencial e devolve apenas o e-mail para a tela de login preencher.
 * Emitir sessão aqui abriria um segundo caminho de emissão de token que pularia o segundo fator, exatamente no tenant onde ele está ligado.
 */
export interface InvitationAcceptance {
  readonly email: string;
}

/**
 * Categorias de falha do aceite de convite, distinguíveis pela UI.
 *
 * A distinção é o requisito, não um detalhe: quem vê "link expirado" pede um convite novo ao administrador, quem vê "serviço indisponível" tenta de novo em minutos, e quem vê "senha fraca" corrige a senha ali mesmo.
 * Colapsar tudo numa mensagem genérica, como manda a regra anti-enumeração do login, seria errado aqui: o token do convite já é o segredo que autentica a operação, então não há o que enumerar, e o convidado precisa saber o que aconteceu.
 */
export type InvitationErrorReason =
  | "invalid_or_expired"
  | "weak_password"
  | "conflict"
  | "service_unavailable"
  | "network"
  | "unknown";

/**
 * Falha tipada do aceite de convite.
 * A mensagem serve a log e depuração; a UI decide a cópia pelo campo `reason`, nunca pelo texto.
 *
 * Nenhum tipo de fornecedor (Firebase, gRPC, corpo bruto de resposta) atravessa esta fronteira: o adapter traduz status HTTP para `reason` e nada mais.
 */
export class InvitationError extends Error {
  readonly reason: InvitationErrorReason;

  constructor(reason: InvitationErrorReason, message: string) {
    super(message);
    this.name = "InvitationError";
    this.reason = reason;
  }
}

/**
 * Resultado da confirmação da redefinição de senha.
 * Deliberadamente NÃO é uma `AuthSession`, pelo mesmo motivo do aceite de convite: a confirmação grava a senha nova e devolve apenas o e-mail para a tela de login preencher.
 * Emitir sessão a partir de um token que chegou por e-mail abriria um caminho de autenticação que pula o segundo fator.
 */
export interface PasswordResetConfirmation {
  readonly email: string;
}

/**
 * Categorias de falha da confirmação de redefinição, distinguíveis pela UI.
 *
 * A distinção é o requisito. Quem vê "link expirado" pede outro link; quem vê "senha recusada" corrige a senha ali mesmo; quem vê "serviço indisponível" tenta de novo em minutos.
 * A regra anti-enumeração vale para o PEDIDO de redefinição, onde qualquer pessoa digita um e-mail e nada pode revelar quem existe. Aqui não há o que enumerar: quem chegou já provou posse do link, que é o segredo que autentica a operação inteira — e colapsar tudo numa frase só faria a pessoa repetir a mesma senha recusada até desistir.
 */
export type PasswordResetErrorReason =
  | "invalid_or_expired"
  | "weak_password"
  | "service_unavailable"
  | "network"
  | "unknown";

/**
 * Falha tipada da confirmação de redefinição de senha.
 * A mensagem serve a log e depuração; a UI escolhe a cópia pelo campo `reason`, nunca pelo texto.
 */
export class PasswordResetError extends Error {
  readonly reason: PasswordResetErrorReason;

  constructor(reason: PasswordResetErrorReason, message: string) {
    super(message);
    this.name = "PasswordResetError";
    this.reason = reason;
  }
}

/** Porta de autenticação consumida por AuthProvider e interceptores HTTP. */
export interface AuthPort {
  /**
   * Primeira etapa do login. Retorna `AuthSession` (login direto) ou `OtpChallenge`
   * (segundo fator exigido — o código foi enviado por e-mail).
   */
  signIn(email: string, password: string): Promise<SignInResult>;
  /** Segunda etapa do login: verifica o código OTP do desafio e retorna a sessão. */
  verifyOtp(challengeId: string, code: string): Promise<AuthSession>;
  signOut(): Promise<void>;
  /** Retorna o ID token corrente; `forceRefresh` força renovação (uso em 401). */
  getIdToken(forceRefresh?: boolean): Promise<string | null>;
  /**
   * Primeiro acesso por convite: consome o token recebido por e-mail, define o nome de exibição e a senha, e devolve o e-mail do convidado.
   * Não autentica — o convidado passa pelo login real, com segundo fator, logo em seguida.
   *
   * Falha com `InvitationError`, cujo `reason` a tela usa para escolher a cópia.
   */
  acceptInvitation(
    token: string,
    displayName: string,
    password: string,
  ): Promise<InvitationAcceptance>;
  /** Dispara e-mail de redefinição de senha (anti-enumeração no chamador). */
  requestPasswordReset(email: string): Promise<void>;
  /**
   * Conclui a redefinição: consome o token recebido por e-mail, grava a senha nova e devolve o e-mail da conta.
   * Não autentica — quem prova que a senha nova funciona é o login real, com segundo fator, logo em seguida.
   *
   * Falha com `PasswordResetError`, cujo `reason` a tela usa para escolher a cópia.
   */
  confirmPasswordReset(token: string, password: string): Promise<PasswordResetConfirmation>;
  /** Observa mudanças de estado de autenticação. Retorna função de unsubscribe. */
  onAuthStateChanged(callback: (session: AuthSession | null) => void): () => void;
}
